import React, { Component } from 'react';
import { createRoot } from 'react-dom/client';

import { observable, computed, action } from 'mobx';
import { observer } from 'mobx-react';

import {
  ThemedShortlistButton,
  ThemedShortlistCount,
} from '../../themes';

import { Date } from '../../events/store/calendar';
import initOrder from './order';

const SHORTLIST_API = '/api/shortlist/';


class Session {
  constructor(data) {
    this.id = data.id;
    this.status = data.status;
    if (data.start_time) {
      this.startDate = new Date(`${ data.start_date }T${ data.start_time }`);
    } else {
      this.startDate = new Date(data.start_date);
    }
    if (data.end_time) {
      this.endDate = new Date(`${ data.endDate }T${ data.endTime }`);
    } else {
      this.endDate = new Date(data.end_date);
    }

    this.venueName = data.venue_name;
    this.thirdPartyId = data.third_party_id;
    this.bookingUrl = data.booking_url;
  }
}


class ShortlistItem {
  eventId;
  sessions = [];

  @observable isShortlisted = false;
  @observable isBuying = false;
  @observable selectedSession = null;
  @observable isChooseYourPrice = false;
  @observable bookingUrl = null;

  constructor(data) {
    this.eventId = data.event_id;
    this.eventTitle = data.event_title;
    this.isShortlisted = data.is_shortlisted;
    this.isChooseYourPrice = data.is_choose_your_price;
    this.bookingUrl = data.event_booking_url;
    if (data.sessions) {
      this.sessions = data.sessions.map(session => new Session(session));
    }

    this.toggleShortlist = this.toggleShortlist.bind(this);
    this.setSelectedSession = this.setSelectedSession.bind(this);
  }

  @action
  toggleBuying = () => this.isBuying = !this.isBuying;

  @action
  toggleShortlist() {
    const url = SHORTLIST_API;
    const method = this.isShortlisted ? 'DELETE' : 'POST';
    const data = JSON.stringify({
      'event_id': this.eventId,
    });

    fetch(url, {
      method,
      body: data,
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then(data => data.json())
      .then(res => {
        this.isShortlisted = !this.isShortlisted;
      });
  }

  @action
  setSelectedSession(id) {
    this.selectedSession = this.getSession(id);
  }

  getSession(id) {
    return this.sessions.find(session => parseInt(id) === session.id);
  }
}


class ShortlistStore {
  @observable items = [];

  @observable isLoading = true;

  @computed get itemsCount() {
    return this.items.filter(item => item.isShortlisted).length;
  }

  @computed get buyingItems() {
    return this.items.filter(item => item.isShortlisted && item.isBuying);
  }

  @computed get buyingItemsCount() {
    return this.buyingItems.length;
  }

  @computed get buyingSessions() {
    return this.buyingItems.filter(item => !!item.selectedSession).map(item => item.selectedSession);
  }

  @computed get buyingSessionsCount() {
    return this.buyingSessions.length;
  }

  @action
  loadItems() {
    return new Promise(resolve => {
      const url = SHORTLIST_API;
      this.isLoading = true;
      fetch(url, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
        .then(data => data.json())
        .then(res => {
          if (res.status === 1) {
            res.items.forEach(item => {
              this.items.push(new ShortlistItem(item));
            });
            this.isLoading = false;
          }
          resolve();
        });
    });
  }

  getItem(eventId) {
    return this.items.find(item => parseInt(eventId) === item.eventId);
  }
}

@observer
class ShortlistCount extends Component {
  render() {
    const { url, shortlistStore } = this.props;
    return (
      <ThemedShortlistCount url={ url } itemsCount={ shortlistStore.itemsCount } />
    );
  }
}

@observer
class ShortlistButton extends Component {
  render() {
    if (typeof GLOBAL_EVENT_SHORTLIST_DISABLED !== 'undefined' && GLOBAL_EVENT_SHORTLIST_DISABLED === true) {
      return null;
    }

    const { item, isLargeBtn, isBannerBtn, isRemoveOption } = this.props;
    // eslint-disable-next-line no-unused-expressions
    item; // `item` seems to be required for all themed buttons to observe state properly.
    return (
      <ThemedShortlistButton
        isShortlisted={ item.isShortlisted }
        isLargeBtn={ isLargeBtn }
        isBannerBtn={ isBannerBtn }
        isRemoveOption={ isRemoveOption }
        onClick={ item.toggleShortlist }
      />
    );
  }
}

const initToggles = store => {
  document.querySelectorAll('[data-shortlist-toggle]').forEach(el => {
    const eventId = el.getAttribute('data-event-id');
    const isShortlisted = el.hasAttribute('data-is-shortlisted');
    const isLargeBtn = el.hasAttribute('data-large-btn');
    const isBannerBtn = el.hasAttribute('data-banner-btn');
    const isRemoveOption = el.hasAttribute('data-remove-option');
    let item = store.getItem(eventId);

    if (!item) {
      item = new ShortlistItem({ 'event_id': eventId, 'is_shortlisted': isShortlisted });
      store.items.push(item);
    }

    const root = createRoot(el);
    root.render(
      <ShortlistButton
        item={ item }
        isLargeBtn={ isLargeBtn }
        isBannerBtn={ isBannerBtn }
        isRemoveOption={ isRemoveOption }
      />
    );
  });
};

const initCount = store => {
  document.querySelectorAll('[data-shortlist-count]').forEach(el => {
    const url = el.getAttribute('data-shortlist-url');
    const root = createRoot(el);
    root.render(
      <ShortlistCount
        url={ url }
        shortlistStore={ store }
      />
    );
  });
};

function init() {
  const store = new ShortlistStore();
  window.STORE = store;

  store.loadItems()
    .then(() => {
      initToggles(store);
      initCount(store);
      initOrder(store);
    });
}

export default { init };
export { ShortlistStore, ShortlistItem, ShortlistButton, ShortlistCount, initToggles, initCount };
